<script setup lang="ts">
import { ref, defineProps, watch, onMounted } from "vue";
import Timezone from "@/components/form-components/Timezone.vue";
import dayjs, { type Dayjs } from "dayjs";
import {
  History as HistoryModel,
} from "../../models";
import type { History } from "@/utils/history";
import BookingModal from "@/components/booking/BookingModal.vue";
import Allotment from "@/components/equipment/Allotment.vue";
import ProductTestInformation from "@/components/product-test/ProductTestInformation.vue";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const props = defineProps({
  timezones: Array,
  year : {
    default: () => (new Date()).getFullYear()
  },
  month : {
    default: () => (new Date()).getMonth()
  },
  equipments: Array,
  tourFilters: Object,
  parks: Array,
  equipmentStatuses: Array,
  allotmentResource: String,
})

const emits = defineEmits([
  'onResetAllotment',
  'onPeriodChange',
]);

const selectedTimezone = ref<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);
const onTimeZoneChange = function (timezone: string) {
  selectedTimezone.value = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const currentMonth = ref<number>(props.month);
const currentYear = ref<number>(props.year);

watch(
  () => props.year,
  (value) => {
    currentYear.value = value
  }
)
watch(
  () => props.month,
  (value) => {
    currentMonth.value = value
  }
)

const navigate = function (move: number) {
  let current = new Date(currentYear.value, currentMonth.value, 1)
  current.setMonth(current.getMonth() + move);

  emits('onPeriodChange', {
    year: current.getFullYear(),
    month: current.getMonth()
  })
}

const emitPeriodChange = () => {
  emits('onPeriodChange', {
    year: currentYear.value,
    month: currentMonth.value
  })
}

watch(
  () => currentMonth.value,
  () => {
    emitPeriodChange()
    getHistories()
  }
)

watch(
  () => currentYear.value,
  () => {
    emitPeriodChange()
    getHistories()
  }
)


const histories = ref([]);
const calendarEvents = ref({});
const isLoading = ref(false);
const getHistories = async function () {
  if (props.equipments?.length === 0) {
    return;
  }
  if (isLoading.value) {
    // Prevent multiple simultaneous loads
    return;
  }
  isLoading.value = true
  histories.value = []

  const startPeriod = dayjs().tz(selectedTimezone.value).set('year', currentYear.value).set('month', currentMonth.value).set('date',1).startOf('month');
  const endPeriod = dayjs().tz(selectedTimezone.value).set('year', currentYear.value).set('month', currentMonth.value).set('date',1).endOf('month');

  try {
    const historyModel = new HistoryModel
    const historyResponse = await historyModel
      .whereIn('equipment_id', props.equipments.map(e => e.id))
      .where('period', [
        startPeriod.format('YYYY-MM-DD'),
        endPeriod.format('YYYY-MM-DD'),
      ].join(':'))
      .limit(-1)
      .get()
    histories.value = historyResponse.data.filter(
      (history: History) => {
        return dayjs(history.start_at).isBefore(endPeriod)
        && (!history.end_at || dayjs(history.end_at).isAfter(startPeriod))
      }
    )
    // setCalendarEvents();
  } catch (e) {
    console.error(e)
  }
  isLoading.value = false
}
watch(
  () => props.equipments,
  () => {
    getHistories()
  }
)

onMounted(() => {
  getHistories()
})

/*
const showTour = ref(false);
const tours = ref([]);
const getTours = async function(){
  try {
    isLoading.value = true
    let tourModel = new TourModel
    Object.entries(props.tourFilters ?? {}).forEach(filter => {
      tourModel = tourModel.where(filter[0], filter[1]);
    });
    const tourResponse = await tourModel
      .include('associatedParks,stages.associatedParks')
      .when(props.parks, (query, parks) => {
        return query.whereIn('park_id', parks.map(p => p.id))
      })
      .where('period', [
        dayjs().set('year', currentYear.value).set('month', currentMonth.value).set('date', 1).startOf('month').format('YYYY-MM-DD'),
        dayjs().set('year', currentYear.value).set('month', currentMonth.value).set('date', 1).endOf('month').format('YYYY-MM-DD'),
      ].join(':'))
      .get()
    tours.value = tourResponse.data
    isLoading.value = false
    setCalendarEvents();
  } catch (e) {
    console.error(e)
  }

}
watch(
  () => showTour.value,
  (value) => {
    if (value) {
      getTours()
    } else {
      tours.value = []
      setCalendarEvents();
    }
  }
)
watch(
  () => props.parks,
  () => {
    getTours()
  }
)

const setCalendarEvents = function () {
  console.log('setCalendarEvents')
  calendarEvents.value = getCalendarEventsFromHistory(props.equipments, histories.value, tours.value)
}
*/

const selectedEquipments = ref([]);
const selectedStart = ref<Dayjs | null>(null);
const selectedEnd = ref<Dayjs | null>(null);
const allotmentEdited = ref(null);
const bookingDetails = ref(null);
const productTestDetails = ref(null);
const showAllotment = ref(false);

const onSelection = function (equipments: [], start: Dayjs, end: Dayjs) {
  selectedEquipments.value = equipments;
  selectedStart.value = start;
  selectedEnd.value = end;

  showAllotment.value = Boolean(start && end && equipments.length > 0);
}
const onClickHistory = function (history: History) {
  if (history.type === 'Allotment') {
    allotmentEdited.value = history.resource
    selectedEquipments.value = [props.equipments.find(e => e.id === history.equipment_id)];
    showAllotment.value = true
  } else if (history.type === 'Booking') {
    bookingDetails.value = history.resource
  } else if (history.type === 'ProductTest') {
    productTestDetails.value = history.resource
  }
}

const resetAllotment = () => {
  selectedEquipments.value = []
  selectedStart.value = null
  selectedEnd.value = null
  allotmentEdited.value = null
  showAllotment.value = false;
  emits('onResetAllotment')
}

const onAllotmentDeleted = () => {
  resetAllotment()
  getHistories()
}
const onAllotmentUpdated = () => {
  resetAllotment()
  getHistories()
}
const onBookingUpdated = () => {
  bookingDetails.value = null
  getHistories()
}
const onProductTestsDialogUpdate = (isOpen) => {
  if (!isOpen) {
    productTestDetails.value = null
  }
}
const onRefreshProductTests = () => {
  productTestDetails.value = null
  getHistories()
}

defineExpose({
  onSelection,
});
</script>

<template>
  <div>
    <div class="container-xl">
      <div class="d-flex flex-column flex-md-row justify-content-end align-items-center">
        <!--
        <button type="button" :class="[
        'btn',
        'btn-light'
        ]" @click.prevent="showTour = !showTour">
          <i :class="[
          'fa',
          showTour ? 'fa-eye-slash' : 'fa-eye'
        ]" aria-hidden="true"></i>
          {{ showTour ? $t('Hide events') : $t('Display events') }}
        </button>
        -->
        <div style="max-width: 300px; width: 100%" class="mb-3">
          <Timezone :timezones="props.timezones" :value="selectedTimezone" @timezone-change="onTimeZoneChange"></Timezone>
        </div>
        <div class="d-flex justify-content-center align-items-center  mb-3">
          <div class="mx-2 d-flex align-items-center">
            <select v-model="currentMonth" class="form-control mr-2">
              <option v-for="m in 12" :value="(m - 1)">{{ (new Date(currentYear, m - 1, 1)).toLocaleDateString($i18n.locale, { month: 'long' }) }}</option>
            </select>
            <input type="number" v-model="currentYear" min="2015" max="TODAY.getFullYear() + 2" class="form-control" style="width: 80px">
          </div>
          <div class="btn-group" role="group">
            <button class="btn btn-light" @click="navigate(-1)">
              <i class="fa fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button class="btn btn-light" @click="navigate(+1)">
              <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <slot
      :isLoading="isLoading"
      :histories="histories"
      :timezone="selectedTimezone"
      :calendarEvents="calendarEvents"
      :onClickHistory="onClickHistory"
    >
    </slot>
  </div>

  <Allotment
    :timezone="selectedTimezone"
    :show="showAllotment"
    :equipments="selectedEquipments"
    :allotmentId="allotmentEdited?.id"
    :equipment-statuses="equipmentStatuses"
    :selected-start="selectedStart?.toISOString()"
    :selected-end="selectedEnd?.toISOString()"
    :resourceUrl="allotmentResource"
    @close="resetAllotment"
    @deleted="onAllotmentDeleted"
    @update="onAllotmentUpdated"
  >
  </Allotment>
  <BookingModal
    :show="!!bookingDetails"
    :booking="bookingDetails"
    @close="bookingDetails = null"
    @update="onBookingUpdated"
  ></BookingModal>
  <Dialog :open="!!productTestDetails" @update:open="onProductTestsDialogUpdate">
    <DialogScrollContent class="tw:bg-zinc-50 tw:sm:max-w-5xl">
      <DialogHeader>
        <DialogTitle>
          {{ $t('Product test information') }}
        </DialogTitle>
      </DialogHeader>
      <ProductTestInformation
        v-if="!! productTestDetails"
        :product-test-id="productTestDetails?.id"
        @refresh="onRefreshProductTests"
      ></ProductTestInformation>
    </DialogScrollContent>
  </Dialog>

</template>

<style scoped>

</style>
